import React, { useMemo, useState } from "react";
import NoData from "../Info/NoData";
import FormFieldSet from "../FormFields/FormFieldSet";
import { buildTreeModel } from "./supportFiles/buildTreeModel";
import { layoutLeftToRight } from "./supportFiles/layoutLeftToRight";
import TreeHierarchyChart from "./supportFiles/TreeHierarchyChart";
import RootJobsModal from "./supportFiles/RootJobsModal";
import BdmJobsModal from "./supportFiles/BdmJobsModal";
import InternalSubmissionModal from "./supportFiles/InternalSubmissionModal";
import StageModal from "./supportFiles/StageModal";

const TreeHierarchy = ({
  data = [],
  duration = {
    from: new Date(),
    to: new Date(),
  },
  onDurationChange = () => {},
  onNodeClick = () => {},
}) => {
  const hasData = Array.isArray(data)
    ? data.length > 0
    : Array.isArray(data?.dates)
      ? data.dates.length > 0
      : false;

  const model = useMemo(() => buildTreeModel(data), [data]);
  const layout = useMemo(() => layoutLeftToRight(model), [model]);
  const [isRootModalOpen, setIsRootModalOpen] = useState(false);
  const [isBdmModalOpen, setIsBdmModalOpen] = useState(false);
  const [isInternalModalOpen, setIsInternalModalOpen] = useState(false);
  const [isStageModalOpen, setIsStageModalOpen] = useState(false);
  const [activeEmployeeId, setActiveEmployeeId] = useState(null);
  const [activeEmployeeIdForStage, setActiveEmployeeIdForStage] =
    useState(null);
  const [activeStageName, setActiveStageName] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  const handleNodeClick = (payload) => {
    if (payload?.type === "root") setIsRootModalOpen(true);
    if (payload?.type === "employee") {
      setActiveEmployeeId(payload?.employeeId ?? null);
      setIsBdmModalOpen(true);
    }
    if (payload?.type === "stage") {
      const stageName = payload?.stageName ?? null;
      const allowed =
        stageName === "Internal Submission" ||
        stageName === "Client Submissions" ||
        stageName === "Interview Round 1" ||
        stageName === "Interview Round 2" ||
        stageName === "Interview Round 3" ||
        stageName === "Placed";

      if (allowed) {
        setActiveEmployeeIdForStage(payload?.employeeId ?? null);
        setActiveStageName(stageName);
        if (stageName === "Internal Submission") setIsInternalModalOpen(true);
        else setIsStageModalOpen(true);
      }
    }
    if (typeof onNodeClick === "function") onNodeClick(payload);
  };

  return (
    <div className="w-full h-full flex flex-1 flex-col overflow-hidden gap-2 ">
      <div className="HeaderTool flex items-center justify-between">
        <div className="min-w-[200px]"></div>
        <div className="min-w-[300px]">
          <FormFieldSet
            title="Select Date"
            noTitle
            type="datepickerRange"
            value={duration}
            onChange={onDurationChange}
            maxDate={new Date()}
          />
        </div>
      </div>
      {!hasData ? (
        <div>
          <NoData title="No data for selected range" />
        </div>
      ) : (
        <div className="w-full flex-1 overflow-hidden">
          <TreeHierarchyChart
            layout={layout}
            onNodeClick={handleNodeClick}
            selectedNodeId={selectedNodeId}
            onNodeSelect={setSelectedNodeId}
          />
        </div>
      )}
      <RootJobsModal
        isOpen={isRootModalOpen}
        onClose={() => {
          setIsRootModalOpen(false);
          setSelectedNodeId(null);
        }}
        data={data}
      />
      <BdmJobsModal
        isOpen={isBdmModalOpen}
        onClose={() => {
          setIsBdmModalOpen(false);
          setActiveEmployeeId(null);
          setSelectedNodeId(null);
        }}
        data={data}
        employeeId={activeEmployeeId}
      />
      <InternalSubmissionModal
        isOpen={isInternalModalOpen}
        onClose={() => {
          setIsInternalModalOpen(false);
          setActiveEmployeeIdForStage(null);
          setActiveStageName(null);
          setSelectedNodeId(null);
        }}
        data={data}
        employeeId={activeEmployeeIdForStage}
        duration={duration}
        stageName={activeStageName || "Internal Submission"}
      />
      <StageModal
        isOpen={isStageModalOpen}
        onClose={() => {
          setIsStageModalOpen(false);
          setActiveEmployeeIdForStage(null);
          setActiveStageName(null);
          setSelectedNodeId(null);
        }}
        data={data}
        employeeId={activeEmployeeIdForStage}
        duration={duration}
        stageName={activeStageName}
      />
    </div>
  );
};

export default TreeHierarchy;
